import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { Avatar, Icon, Tooltip } from '@components/index';
import { AccountType, OrgRoleType } from '@constants/generic';
import { LEAD_MANAGEMENT_ROLES } from '@constants/role';
import { DASHBOARD, LEAD_MANAGEMENT } from '@constants/routes';
import { STORAGE_KEYS } from '@constants/storage';
import { Event, sendMixpanelEvent } from '@core/analytics/mixpanel';
import { RoleType } from '@features/team-management/constants';
import { setMultipleLocalStorageItems } from '@utils/storage';
import { Org } from 'types/organization';

import { updateBranchFilter } from '../branchSlice';

import theme from '../../../../themes/colors';

interface OrgAccountListProps {
  currentOrg: Org;
  orgList: Org[];
  setShowOrgSwitcherModal: (show: boolean) => void;
  setShowOrganizationDetailsModal: (show: boolean) => void;
}

const OrgAccountList = ({
  currentOrg,
  orgList,
  setShowOrgSwitcherModal,
  setShowOrganizationDetailsModal
}: OrgAccountListProps) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const userRole = currentOrg?.role as OrgRoleType;

  const showOrgEditButton = [
    OrgRoleType.OWNER,
    OrgRoleType.ORGANISATION_ADMIN,
    OrgRoleType.INDIVIDUAL
  ].includes(userRole);

  const bgColorList = [
    theme.cofeeBlue50,
    theme.cofeeOrange50,
    theme.cofeePurple50,
    theme.cofeeGreen1
  ];

  const filteredOrgList = orgList?.filter((org) => org.id !== currentOrg?.id);

  const onSwitchOrg = (selectedOrg: Org) => {
    if (selectedOrg) {
      const {
        id,
        default_branch_id: defaultBranchId,
        default_branch_name: defaultBranchName,
        role
      } = selectedOrg;
      setMultipleLocalStorageItems([
        { key: STORAGE_KEYS.ORGANIZATION_ID, value: id },
        { key: STORAGE_KEYS.BRANCH_ID, value: defaultBranchId },
        { key: STORAGE_KEYS.BRANCH_NAME, value: defaultBranchName }
      ]);
      dispatch(
        updateBranchFilter({
          branchId: defaultBranchId,
          branchName: defaultBranchName
        })
      );
      if (LEAD_MANAGEMENT_ROLES.includes(role as unknown as RoleType)) {
        navigate(LEAD_MANAGEMENT.LEAD_DASHBOARD);
      } else {
        navigate(DASHBOARD);
      }
      setShowOrgSwitcherModal(false);
      sendMixpanelEvent(Event.SWITCH_ACCOUNT_CONTINUE_BUTTON_CLICKED, {
        switched_to: selectedOrg.name
      });
    }
  };

  const onEditOrganizationClick = () => {
    setShowOrgSwitcherModal(false);
    setShowOrganizationDetailsModal(true);
  };

  return (
    <div className="absolute left-4 z-[45] w-[350px] min-w-350 max-w-[350px] cursor-default rounded-2xl border border-cofeeNeutral5 bg-cofeeNeutral1 shadow-boxShadow1">
      <div className="flex h-20 items-center justify-between gap-2 overflow-hidden p-4">
        <div className="flex items-center gap-2">
          <Avatar
            name={currentOrg?.name}
            className="size-12 min-w-12 rounded-lg text-theme"
            imageUrl={currentOrg?.image_url}
            bgColor={bgColorList[2]}
            textClassName="text-lg font-medium text-cofeeNeutral10"
          />
          <div className="flex w-full flex-col justify-center truncate  break-words py-1">
            <div className="max-w-50 overflow-hidden text-ellipsis text-lg font-semibold text-cofeeNeutral10">
              {currentOrg?.type === AccountType.ORGANIZATION
                ? currentOrg?.name
                : currentOrg?.display_name}
            </div>
            <div className="text-cofeeNeutral7">{currentOrg?.role_name}</div>
          </div>
        </div>
        {showOrgEditButton && (
          <>
            <div
              className="flex !size-8 cursor-pointer items-center justify-center rounded-lg hover:bg-cofeeGreyBlue75"
              onClick={onEditOrganizationClick}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  setShowOrganizationDetailsModal(true);
                  setShowOrgSwitcherModal(false);
                }
              }}
              tabIndex={0}
              role="button"
              data-tooltip-id="edit-org-icon"
            >
              <Icon name="edit-pencil" size="16" />
            </div>
            <Tooltip id="edit-org-icon" type="text" tooltipText="Edit" />
          </>
        )}
      </div>
      {filteredOrgList.length > 0 && (
        <div className="pb-4 pl-4">
          <p className="border-t border-cofeeNeutral5 pb-2 pt-4 text-sm font-medium text-cofeeNeutral8">
            Switch accounts
          </p>
          <div className="custom-scroll mr-5px max-h-[476px] overflow-auto  pr-[5px] ">
            {filteredOrgList.map((org, index) => (
              <div
                className={`my-2 flex cursor-pointer items-center gap-2 pb-2 last:!mb-0 last:!pb-0 ${
                  index === filteredOrgList.length - 1
                    ? 'border-b-0'
                    : 'border-b border-cofeeNeutral5'
                }`}
                key={org.id}
                onClick={() => onSwitchOrg(org)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    onSwitchOrg(org);
                  }
                }}
                tabIndex={0}
                role="button"
              >
                <Avatar
                  name={org?.name}
                  className="size-12 min-w-12 rounded-lg text-theme"
                  imageUrl={org?.image_url}
                  bgColor={bgColorList[index % bgColorList.length]}
                  textClassName="text-lg font-medium text-cofeeNeutral10"
                />
                <div className="flex w-full flex-col justify-center truncate  break-words py-1">
                  <div className="overflow-hidden text-ellipsis font-medium text-cofeeNeutral10">
                    {org?.type === AccountType.ORGANIZATION
                      ? org?.name
                      : org?.display_name}
                  </div>
                  <div className="text-sm font-medium text-cofeeNeutral8">
                    {org?.role_name}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default OrgAccountList;
